import Sidebar from "../components/Sidebar";
import MobileHeader from "../components/MobileHeader";
import useMoveTo from "../constants/navigation";
import { ROUTES } from "../App";
import { ICONS } from "../constants/icons";
import { BsBriefcase, BsSearch, BsListUl, BsArrowRight, BsStars } from "react-icons/bs";

/* ─── Glassmorphism card styles ─────────────────────────────────────── */
const seekerGlow = {
    border: "1px solid rgba(34, 211, 238, 0.35)",
    boxShadow:
        "0 0 0 1px rgba(34,211,238,0.08), 0 8px 32px rgba(34,211,238,0.12), inset 0 1px 0 rgba(255,255,255,0.06)",
    background:
        "linear-gradient(135deg, rgba(34,211,238,0.07) 0%, rgba(14,165,233,0.04) 100%)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
};

const employerGlow = {
    border: "1px solid rgba(99, 102, 241, 0.35)",
    boxShadow:
        "0 0 0 1px rgba(99,102,241,0.08), 0 8px 32px rgba(99,102,241,0.12), inset 0 1px 0 rgba(255,255,255,0.06)",
    background:
        "linear-gradient(135deg, rgba(99,102,241,0.07) 0%, rgba(139,92,246,0.04) 100%)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
};

const seekerIconBg = {
    background:
        "linear-gradient(135deg, rgba(34,211,238,0.25) 0%, rgba(14,165,233,0.15) 100%)",
    boxShadow: "0 4px 20px rgba(34,211,238,0.3), inset 0 1px 0 rgba(255,255,255,0.15)",
    border: "1px solid rgba(34,211,238,0.4)",
};

const employerIconBg = {
    background:
        "linear-gradient(135deg, rgba(99,102,241,0.25) 0%, rgba(139,92,246,0.15) 100%)",
    boxShadow:
        "0 4px 20px rgba(99,102,241,0.3), inset 0 1px 0 rgba(255,255,255,0.15)",
    border: "1px solid rgba(99,102,241,0.4)",
};

/* ─── Radial background glow (dark mode only) ─────────────────────── */
const bgGlow = {
    background:
        "radial-gradient(ellipse 70% 50% at 25% 40%, rgba(34,211,238,0.06) 0%, transparent 60%), " +
        "radial-gradient(ellipse 60% 50% at 75% 60%, rgba(99,102,241,0.07) 0%, transparent 60%)",
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
};

const JobsLanding = () => {
    const navigate = useMoveTo();

    return (
        <div className="flex flex-col h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-hidden">
            <MobileHeader title="Jobs & Hiring" isBackBtn={true} />
            <div className="flex flex-1 overflow-hidden">
                <Sidebar />
                <main className="relative flex-1 overflow-y-auto pb-24 md:pb-5 bg-white dark:bg-[#1A1A1A]">
                    {/* Radial glow — dark mode only */}
                    <div className="hidden dark:block" style={bgGlow} aria-hidden="true" />

                    <div className="relative z-10 flex flex-col items-center justify-center min-h-full px-6 py-10">
                        {/* Header */}
                            <div className="flex items-center justify-between w-full mb-8 md:mb-12">
                                <button
                                    onClick={() => navigate(-1)}
                                    className="flex-shrink-0 md:block hidden"
                                >
                                    <img
                                        src={ICONS.arrowBack}
                                        className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert"
                                        alt="Back"
                                    />
                                </button>
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold
                                    bg-cyan-50 text-cyan-600 border border-cyan-200
                                    dark:bg-cyan-500/10 dark:text-cyan-400 dark:border-cyan-500/20">
                                    <BsStars className="w-3 h-3" />
                                    Creative Opportunities
                                </div>
                                <div className="w-5 md:block hidden" />
                            </div>
                            <div className="text-center">
                                <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">
                                    Jobs & Hiring
                                </h1>
                                <p className="text-sm md:text-base text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                                    Connect with creative talent and clients in your niche
                                </p>
                            </div>

                        {/* Cards */}
                        <div className="w-full max-w-3xl grid grid-cols-1 md:grid-cols-2 gap-5">
                            {/* Card 1 — Job Seeker */}
                            <button
                                onClick={() => navigate(ROUTES.JOBS_FIND)}
                                className="group relative p-7 rounded-3xl text-left transition-all duration-300
                                    hover:scale-[1.02] active:scale-[0.99]
                                    bg-gray-50 border border-gray-200
                                    dark:bg-transparent"
                                style={{ ...seekerGlow }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.boxShadow =
                                        "0 0 0 1px rgba(34,211,238,0.2), 0 16px 48px rgba(34,211,238,0.2), inset 0 1px 0 rgba(255,255,255,0.08)";
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.boxShadow =
                                        "0 0 0 1px rgba(34,211,238,0.08), 0 8px 32px rgba(34,211,238,0.12), inset 0 1px 0 rgba(255,255,255,0.06)";
                                }}
                            >
                                {/* Icon */}
                                <div
                                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                                    style={seekerIconBg}
                                >
                                    <BsSearch className="w-6 h-6 text-cyan-400" />
                                </div>

                                <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">
                                    I{"'"}m looking for a job
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-6">
                                    Discover freelance gigs and creative projects that match your skills.
                                </p>

                                <div className="flex items-center gap-1.5 text-sm font-semibold text-cyan-600 dark:text-cyan-400">
                                    Browse listings
                                    <BsArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                                </div>
                            </button>

                            {/* Card 2 — Employer */}
                            <button
                                onClick={() => navigate(ROUTES.JOBS_POST)}
                                className="group relative p-7 rounded-3xl text-left transition-all duration-300
                                    hover:scale-[1.02] active:scale-[0.99]
                                    bg-gray-50 border border-gray-200
                                    dark:bg-transparent"
                                style={{ ...employerGlow }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.boxShadow =
                                        "0 0 0 1px rgba(99,102,241,0.2), 0 16px 48px rgba(99,102,241,0.2), inset 0 1px 0 rgba(255,255,255,0.08)";
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.boxShadow =
                                        "0 0 0 1px rgba(99,102,241,0.08), 0 8px 32px rgba(99,102,241,0.12), inset 0 1px 0 rgba(255,255,255,0.06)";
                                }}
                            >
                                {/* Icon */}
                                <div
                                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                                    style={employerIconBg}
                                >
                                    <BsBriefcase className="w-6 h-6 text-indigo-400" />
                                </div>

                                <h3 className="text-lg font-bold mb-2 text-gray-900 dark:text-white">
                                    I want to hire someone
                                </h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-6">
                                    Post a job and connect with the right creative talent for your project.
                                </p>

                                <div className="flex items-center gap-1.5 text-sm font-semibold text-indigo-500 dark:text-indigo-400">
                                    Post a job
                                    <BsArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                                </div>
                            </button>
                        </div>

                        {/* My Posts link */}
                        <button
                            onClick={() => navigate(ROUTES.JOBS_MY_POSTS)}
                            className="flex items-center gap-2 mt-8 text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors font-medium"
                        >
                            <BsListUl className="w-4 h-4" />
                            View my job posts
                        </button>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default JobsLanding;
